import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Target, ArrowLeft, Plus, ChevronRight, CalendarClock } from "lucide-react";
import { listGoals } from "@/lib/api";
import { daysUntil, formatHorizon, formatLongDate } from "./helpers";

export default function GoalsList() {
    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(true);
    const nav = useNavigate();

    useEffect(() => {
        listGoals().then(d => setGoals(d.goals || [])).finally(() => setLoading(false));
    }, []);

    const buckets = useMemo(() => {
        const b = { active: [], resting: [], completed: [] };
        goals.forEach(g => (b[g.status] || b.active).push(g));
        return b;
    }, [goals]);

    return (
        <div
            data-testid="goals-page"
            className="px-5 md:px-8 py-6 md:py-8 pb-32 mx-auto w-full"
            style={{ maxWidth: "72ch" }}
        >
            <button
                onClick={() => nav("/")}
                className="inline-flex items-center gap-1.5 font-ui text-[13px] text-ink-70 hover:text-ink mb-3"
            >
                <ArrowLeft size={14} /> Home
            </button>

            <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-3">
                    <Target size={22} className="text-moss-deep" />
                    <h1 className="scripture text-3xl md:text-4xl text-ink tracking-tight">Goals</h1>
                </div>
                <Link
                    to="/goals/new"
                    data-testid="goal-new-btn"
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-moss text-white font-ui text-[12px] font-medium hover:bg-moss-deep transition"
                >
                    <Plus size={14} /> New goal
                </Link>
            </div>
            <p className="font-ui text-[13px] text-ink-70 mb-6">
                <span className="italic">"Write the vision. Make it plain."</span> <span className="text-ink-40">— Habakkuk 2:2</span> · What are you working toward?
            </p>

            {loading ? (
                <p className="font-ui text-sm text-ink-40">Loading…</p>
            ) : goals.length === 0 ? (
                <EmptyGoals />
            ) : (
                <>
                    <Section title="Active" testid="goals-active" list={buckets.active} />
                    {buckets.resting.length > 0 && (
                        <Section title="Resting" testid="goals-resting" list={buckets.resting} muted />
                    )}
                    {buckets.completed.length > 0 && (
                        <Section title="Completed" testid="goals-completed" list={buckets.completed} muted />
                    )}
                </>
            )}
        </div>
    );
}

function EmptyGoals() {
    return (
        <div data-testid="goals-empty" className="mt-14 text-center max-w-md mx-auto">
            <div className="scripture text-[24px] md:text-[26px] text-ink leading-snug">
                What are you working toward?
            </div>
            <p className="mt-2 font-ui text-[14px] italic text-ink-70">
                "Write the vision. Make it plain." — Habakkuk 2:2
            </p>
            <Link
                to="/goals/new"
                className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-moss text-white font-ui text-[13px] font-medium hover:bg-moss-deep transition"
            >
                <Plus size={14} /> Create a goal
            </Link>
        </div>
    );
}

function Section({ title, testid, list, muted = false }) {
    return (
        <section data-testid={testid} className="mt-6">
            <h2 className="font-ui text-[10px] uppercase tracking-[0.25em] text-ink-40 font-semibold mb-2">
                {title} · {list.length}
            </h2>
            <ul className="space-y-2">
                {list.map(g => (
                    <li key={g.id}>
                        <Link
                            to={`/goals/${g.id}`}
                            data-testid={`goal-card-${g.id}`}
                            className={
                                "block p-4 rounded-2xl border transition " +
                                (muted
                                    ? "bg-sage-paper border-ink-10 hover:border-moss/40"
                                    : "bg-white border-ink-10 hover:border-moss/40 hover:shadow-md")
                            }
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="px-2 py-0.5 rounded-full bg-moss/10 text-moss-deep font-ui text-[10px] font-medium">
                                            {g.category}
                                        </span>
                                        {g.theme && (
                                            <span className="font-ui text-[10px] text-ink-40 italic">{g.theme}</span>
                                        )}
                                        {g.rhythm !== "none" && (
                                            <span className="font-ui text-[10px] text-ink-40 inline-flex items-center gap-1">
                                                <CalendarClock size={10} /> {g.rhythm === "times_of_day" ? "times of day" : g.rhythm}
                                            </span>
                                        )}
                                        {g.target_date && (() => {
                                            const days = daysUntil(g.target_date);
                                            const amber = days !== null && days >= 0 && days <= 7;
                                            return (
                                                <span
                                                    data-testid={`goal-horizon-${g.id}`}
                                                    data-amber={amber ? "yes" : "no"}
                                                    className={
                                                        "font-ui text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full " +
                                                        (amber ? "text-ink font-medium" : "text-ink-40")
                                                    }
                                                    style={amber ? { backgroundColor: "rgba(231, 195, 111, 0.35)" } : undefined}
                                                    title={formatLongDate(g.target_date)}
                                                >
                                                    <CalendarClock size={10} /> {formatHorizon(g.target_date)}
                                                </span>
                                            );
                                        })()}
                                    </div>
                                    <div className="scripture text-[18px] text-ink mt-1 leading-snug">
                                        {g.title}
                                    </div>
                                </div>
                                <ChevronRight size={18} className="shrink-0 text-ink-40" />
                            </div>
                        </Link>
                    </li>
                ))}
            </ul>
        </section>
    );
}
